<template lang="pug">

.hooper
  section.hooper-intro
    .hooper-intro-content
      div
        AppTitle.u-mb-sm(:title="$t('mantra')")

        AppLink(to="https://baianat.github.io/hooper/examples.html" class="button-link is-white" :title="$t('demo')") {{$t('demo')}}

    .hooper-intro-img: img( src='https://cdn.baianat.com/static/resources/hooper/1.png', srcset='https://cdn.baianat.com/static/resources/hooper/1.png 1x, https://cdn.baianat.com/static/resources/hooper/1@2x.png 2x')


  section.u-textCenter.u-mt-lg
    .grid.is-center.is-ultrawide-5.is-widescreen-6.is-desktop-7.is-tablet-8
      .column
        h5 {{$t('createBeautiful')}}

  section.hooper-features.u-mt-lg
    .grid.is-ultrawide-8.is-widescreen-9.is-desktop-10.is-center
      .column
        h2.u-mb-mi {{$t('features')}}
        .grid.is-mb.is-tablet-6
          .column(v-for="item in $t('featuresItems')")
            h4 {{item.title}}
            p {{item.desc}}

  section.u-mt-lg

    .grid.is-mb.is-middle
      .column.is-desktop-6.is-tablet-6
        img( src='https://cdn.baianat.com/static/resources/hooper/2.png', srcset='https://cdn.baianat.com/static/resources/hooper/2.png 1x, https://cdn.baianat.com/static/resources/hooper/2@2x.png 2x')
      .column.is-desktop-4.is-tablet-6
        h3 {{$t('smoothTransitions')}}
        p {{$t('smoothTransitionsDesc')}}

    .grid.is-mb.is-middle
      .column.is-desktop-4.is-tablet-6
        h3 {{$t('crossBrowser')}}
        p {{$t('crossBrowserDesc')}}
      .column.is-desktop-6.is-tablet-6
        img( src='https://cdn.baianat.com/static/resources/hooper/3.png', srcset='https://cdn.baianat.com/static/resources/hooper/3.png 1x, https://cdn.baianat.com/static/resources/hooper/3@2x.png 2x')


  section.verte-install.u-mt-lg
    .grid.is-ultrawide-8.is-widescreen-9.is-desktop-10.is-center
      .column
        AppTitle.u-mb-sm(:title="$t('installation')" tag="h2")

        prism(language="js" :code="install")


  section.hooper-cta.u-mt-lg
    AppTitle(:title="$t('swipeImages')" tag="h2")
    AppLink(to="https://baianat.github.io/hooper/" class="button-link is-white" :title="$t('getStarted')") {{$t('getStarted')}}



  .u-mb-md

</template>
<style lang="stylus">
$clMain = #004a69
$clGreen = #00a14f

.app.is-hooper
  overflow: hidden

  &.is-dark
    .header.is-scroll
      background: $clMain

.hooper
  &-intro
    padding-top: unitRes(60, 100)
    margin-left: -12vw
    margin-right: -12vw
    padding-right: 4vw
    padding-left: 4vw
    z-index: 0
    position: relative
    color: $clWhite
    text-align: center

    &-content
      position: absolute
      top: 0
      left: 0
      right: 0
      bottom: 0
      display: flex
      align-items: center
      justify-content: center
      z-index: 2

      h1
        text-shadow: 1px 1px 11px alpha($clBlack, 0.1)

      .button-link.is-white:hover, .button-link.is-white.is-active
        color: #00dbff
        background-image: linear-gradient(to right, #00dbff 0%, #00dbff 100%)

    &-img
      +mq($until: 1900px)
        width: 160%
        transform: translateX(-20%)

      +mq($until: 'tablet')
        width: 200%
        transform: translateX(-25%)

      +mq($until: 'mobile')
        width: 300%
        transform: translateX(-33.5%)

  &-cta
    background-color: $clBlack
    padding: unitRes(20, 160)
    color: $clWhite
    background-image: url('https://cdn.baianat.com/static/resources/hooper/4.png')
    background-repeat: no-repeat
    background-position: 80% 120%
    background-attachment: fixed

    h2, a
      text-shadow: 1px 1px 11px alpha($clBlack, 0.3)

html[lang^='ar']
  .hooper
    &-cta
      background-position: 20% 120%
</style>

<script>
import Prism from '~/components/Prism';
import { generateSeoMeta } from '~/utils/seo';

export default {
  scrollToTop: true,
  meta: {
    theme: 'is-hooper ',
    logoText: '__NONE__',
    logoIcon: 'logoHooperTypo'
  },
  head: generateSeoMeta(
    {
      title: 'pageTitle',
      description: 'pageDesc'
    },
    { i18n: true }
  ),

  components: {
    Prism
  },
  data: () => ({
    install: `npm install hooper
# or use yarn
yarn add hooper

# Usage
<template>
  <hooper>
    <slide>
      slide 1
    </slide>
    <slide>
      slide 2
    </slide>
    ...
  </hooper>
<template>

<script>
  import { Hooper, Slide } form 'hooper';

  export default {
    name: 'App',
    components: {
      Hooper,
      Slide
    }
  }
<\/script>
`
  })
};
</script>

<i18n>
{
  "en": {
    "pageTitle":"Hooper",
    "pageDesc":"Hooper lets you develop beautiful carousel sliders that scale with different sizes and comes with multiple configurations.",
    "mantra":"A simple responsive |carousel.",
    "demo":"Demo",
    "createBeautiful":"Hooper lets you create beautiful carousel sliders that scale well in different sizes, with multiple configurations and settings to come up with your very own image slider.",
    "features":"Features",
    "featuresItems":{
      "1":{
        "title":"Lightweight",
        "desc":"Built using vanilla JavaScript. No jQuery, no dependencies."
      },
      "2":{
        "title":"Fully responsive",
        "desc":"Enjoy Hooper on multiple view ports and different scenarios, as it scales well in different sizes."
      },
      "3":{
        "title":"Touch support",
        "desc":"Designed with mobile devices in mind as well as mouse dragging action for desktops."
      },
      "4":{
        "title":"SVG ready",
        "desc":"Hooper works with SVGs for custom navigation icons as well to provide an even smoother experience."
      }
    },
    "smoothTransitions":"Smooth transitions",
    "smoothTransitionsDesc":"Hooper provides a smooth transition experience between sliders, and you can set your own transitions as well.",
    "crossBrowser":"Cross-browser compatibility",
    "crossBrowserDesc":"Hooper is compatible with all browsers and provides the same consistent experience.",
    "installation":"Installation |and Usage.",
    "swipeImages":"Swipe images |with ease.",
    "getStarted":"Get started"
  },
  "ar": {
    "pageTitle":"Hooper",
    "pageDesc":"يتيح لك Hooper إنشاء carousel sliders بشكل جيد في أحجام مختلفة، مع تكوينات وإعدادات متعددة لشريط تمرير الصور.",
    "mantra":"Carousel بسيط |و متجاوب.",
    "demo":"عرض",
    "createBeautiful":"يوفر لك Hooper إنشاء carousel sliders فريدة والتي تتناسب مع الأحجام المختلفة، مع تكوينات وإعدادات متعددة لتكوين شريط التمرير الخاص بك.",
    "features":"المميزات",
    "featuresItems":{
      "1":{
        "title":"خفيف",
        "desc":"تم بناؤه باستخدام vanilla JavaScript وليس  jQuery أو حتى dependencies."
      },
      "2":{
        "title":"استجابة كاملة",
        "desc":"استمتع من خلال Hooper بمنافذ عرض متعددة وسيناريوهات مختلفة، حيث يتنوع و يتناسب مع الأحجام المختلفة."
      },
      "3":{
        "title":"يدعم اللمس",
        "desc":"تم مراعاة أجهزة الجوال أثناء تصميم Hooper، بالإضافة إلى إجراءات السحب التى تتم عبر الماوس لأجهزة الكمبيوتر."
      },
      "4":{
        "title":"SVG جاهزة",
        "desc":"يعمل Hooper مع SVGs لرموز التنقل المخصصة أيضًا؛ لتوفير تجربة أكثر سلاسة."
      }
    },
    "smoothTransitions":"سلاسة الانتقالات",
    "smoothTransitionsDesc":"يوفر Hooper تجربة انتقالات بسيطة بين أشرطة التمرير، ويمكنك ضبط الانتقالات الخاصة بك بسهولة.",
    "crossBrowser":"التوافق مع المتصفحات",
    "crossBrowserDesc":"يتوافق Hooper مع جميع المتصفحات كما أنه يوفر نفس الخبرة المتناغمة.",
    "installation":"التثبيت |والاستخدام",
    "swipeImages":"حرِك الصور |بسهولة.",
    "getStarted":"ابدأ الآن."
  }
}
</i18n>
